import { Injectable } from '@angular/core';
import { first, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { VPSConfiguration, VPSPackConfigModel } from './vps.model';

@Injectable()
export class VpsService {
  currency = window['CONST_CURRENCY'].id;
  account = window['CURRENT_ACCOUNT'] ? window['CURRENT_ACCOUNT'].id : null;

  constructor(private http: HttpClient) {}

  getPrices(country: string) {
    return this.http
      .get(
        '/api/hosting/prices?currency_id=' +
          this.currency +
          (this.account ? '&account_id=' + this.account : '') +
          ('&country=' + country) +
          '&prices[]=vps_only'
      )
      .pipe(
        first(),
        map((res: any) => {
          if (res.data.vps_only?.prices) {
            return {
              managed: this.formatConfig(res.data.vps_only.prices.managed?.[1]),
              unmanaged: this.formatConfig(res.data.vps_only.prices.unmanaged?.[1]),
              lite: this.formatConfig(res.data.vps_only.prices.lite?.[1]),
            };
          } else {
            return null;
          }
        })
      );
  }

  formatConfig(config): VPSConfiguration['lite'] | VPSConfiguration['managed'] | VPSConfiguration['unmanaged'] {
    const result = {
      configs: Object.entries(config.packs).map(([packId, pack]: [string, any]): VPSPackConfigModel => {
        return {
          id: parseInt(packId, 10),
          cpu: pack.cpu_number,
          ram: pack.ram_number,
          hdd: pack.hdd_system_number,
          windowsCompatible: pack.can_use_windows,
          canBeOrdered: pack.can_be_ordered,
          prices: {
            monthly: {
              setup_price_excl_vat: pack.price.price.excl_vat,
              setup_price_incl_vat: pack.price.price.incl_vat,
              setup_original_price_excl_vat: pack.price.before_discount.excl_vat,
              setup_original_price_incl_vat: pack.price.before_discount.incl_vat,
              promotion: pack.price.promotion,
            },
          },
          site_include: pack.site.min_value,
          ssl_include: pack.ssl.min_value,
          disk_include: pack.hdd.min_value,
          hosting_include: pack.hosting.min_value,
          snapshot_include: pack.snapshot.min_value,
          loading: false,
          stared: pack.cpu_number === 6,
        };
      }),
      storageConfigs:
        config.hdd?.packs &&
        Object.values(config.hdd?.packs).map((storage: number, index) => {
          return {
            id: index + 1,
            storage: storage,
            prices: {
              monthly: {
                setup_price_excl_vat: config.hdd?.prices[storage].price.excl_vat,
                setup_price_incl_vat: config.hdd?.prices[storage].price.incl_vat,
                setup_original_price_excl_vat: config.hdd?.prices[storage].before_discount.excl_vat,
                setup_original_price_incl_vat: config.hdd?.prices[storage].before_discount.incl_vat,
                promotion: config.hdd?.prices[storage].promotion,
              },
            },
            loading: false,
            stared: storage === 500,
          };
        }),
      storageConfigSpe: {
        id: 4,
        storage: config.hdd?.max_value,
        storageFC: new FormControl(config.hdd?.max_value),
        onChange: false,
        prices: [],
        step: parseInt(config.hdd?.step),
        min_value: config.hdd?.min_value,
        max_value: config.hdd?.max_value,
        loading: false,
        stared: false,
      },
      vpsTypeCanBeOrdered: config.vps_type_can_be_ordered,
    };

    const step = parseInt(config.hdd?.step);
    for (let i = config.hdd?.min_value; i <= config.hdd?.max_value; i += step) {
      result.storageConfigSpe.prices[i] = {
        monthly: {
          setup_price_excl_vat: config.hdd?.prices[i].price.excl_vat,
          setup_price_incl_vat: config.hdd?.prices[i].price.incl_vat,
          setup_original_price_excl_vat: config.hdd?.prices[i].before_discount.excl_vat,
          setup_original_price_incl_vat: config.hdd?.prices[i].before_discount.excl_vat,
          promotion: config.hdd?.prices[i].promotion,
        },
      };
    }

    return result;
  }
}
